import React, { useState } from "react";
import { Button, Notification } from "@rio-cloud/rio-uikit";
import ConfirmDialog from "../Dialogs/Confirm";
import { disarmUrgentAlert } from "src/services/trips";
import { IUrgentAlert } from "@types";
import { formatDateBrUtc } from "@utils";

interface UrgentAlertBannerItemProps {
  alert: IUrgentAlert;
  showDisarmButton?: boolean;
  onDisarmed?: (alertId: string) => void;
}

const UrgentAlertBannerItem = ({ alert, showDisarmButton = false, onDisarmed }: UrgentAlertBannerItemProps) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDisarm = async () => {
    try {
      setLoading(true);
      await disarmUrgentAlert(alert.tripId, {
        routeId: alert.routeId,
        latitude: alert.latitude,
        longitude: alert.longitude,
      });
      setShowConfirm(false);
      Notification.success("Alerta urgente desarmado com sucesso.");
      onDisarmed?.(alert.id);
    } catch (error) {
      console.error("Erro ao desarmar alerta urgente:", error);
      Notification.error("Não foi possível desarmar o alerta urgente.");
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = alert.createdAt
    ? formatDateBrUtc(alert.createdAt)
    : null;

  const isDisarmed = !!alert.disarmedAt;

  const formattedCoords =
    alert.latitude && alert.longitude
      ? `${alert.latitude.toFixed(5)}, ${alert.longitude.toFixed(5)}`
      : null;

  return (
    <>
      <div
        className="alert alert-danger shadow-sm"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          margin: "10px 20px",
          padding: "12px 16px",
          border: "none",
          borderRadius: "4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
          <div style={{ flexShrink: 0, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="rioglyph rioglyph-warning-sign text-size-h4 text-color-danger" />
          </div>

          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span className="text-size-14" style={{ fontWeight: 500 }}>Alerta urgente enviado pelo motorista</span>
            {formattedDate && (
              <span className="text-size-14 text-color-dark">{formattedDate}</span>
            )}
            {formattedCoords && (
              <span className="text-size-12 text-color-gray">
                <span className="rioglyph rioglyph-map-marker margin-right-3" />
                {formattedCoords}
              </span>
            )}
          </div>
        </div>

        {showDisarmButton && !isDisarmed && (
          <div style={{ flexShrink: 0 }}>
            <Button
              variant="outline"
              className="border-color-danger text-color-danger"
              onClick={() => setShowConfirm(true)}
              disabled={loading}
              style={{
                borderRadius: "8px",
                padding: "8px 20px",
                backgroundColor: "var(--always-color-white)",
                cursor: "pointer",
                transition: "none",
                whiteSpace: "nowrap",
              }}
            >
              {loading ? "Desarmando..." : "Desarmar alerta"}
            </Button>
          </div>
        )}

      </div>

      <ConfirmDialog
        show={showConfirm}
        title="Desarmar Alerta Urgente"
        content="Tem certeza que deseja desarmar o alerta urgente enviado pelo motorista?"
        onClickConfirm={handleDisarm}
        onClickCancel={() => setShowConfirm(false)}
        confirmButtonText="Sim, desarmar"
        cancelButtonText="Cancelar"
      />
    </>
  );
};

interface UrgentAlertBannerListProps {
  alerts: IUrgentAlert[];
  onDisarmed?: (alertId: string) => void;
}

const UrgentAlertBannerList = ({ alerts, onDisarmed }: UrgentAlertBannerListProps) => {
  if (!alerts || alerts.length === 0) return null;

  const sorted = [...alerts].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div>
      {sorted.map((alert, index) => (
        <UrgentAlertBannerItem
          key={alert.id}
          alert={alert}
          showDisarmButton={index === 0}
          onDisarmed={onDisarmed}
        />
      ))}
    </div>
  );
};

export { UrgentAlertBannerItem };
export default UrgentAlertBannerList;
